/**
 * Child Project Name — single source of truth for the split/compose logic used by
 * the "optional suffix" input that appears in TWO places for the same value:
 *
 *   - ParentProjectTab.tsx  → "Child Project Names" rows on the Project Setup tab
 *   - ChildSurveyTab.tsx    → "Child Project Name" on each country tab
 *
 * Both are backed by childStates[childKey].childProjectName, which stores the FULL
 * string `${parentName}_${countryCode}` (no suffix) or `${parentName}_${countryCode}_${suffix}`.
 * The PM only ever edits the suffix; the locked prefix is rendered as static text.
 *
 * WHITESPACE RULE (this is why these helpers exist):
 * the stored value IS the live value of a controlled input, so nothing on the
 * read path may trim it. Trimming there deletes the space the PM just typed
 * before the next render, which makes multi-word names impossible to type
 * ("Spanish Wave 2" collapses to "SpanishWave2"). Whitespace is normalized in
 * exactly one place — normalizeChildProjectName, called at submit time.
 */

export interface ChildProjectNameParts {
  /** `${parentName}_${countryCode}` — what's stored when there is no suffix. */
  base: string;
  /** `${parentName}_${countryCode}_` — the locked prefix shown beside the input. */
  prefix: string;
  /** Legacy rows created while the parent name was still empty: `_${countryCode}`. */
  orphanBase: string;
  /** Legacy rows created while the parent name was still empty: `_${countryCode}_`. */
  orphanPrefix: string;
}

export function childProjectNameParts(parentName: string, countryCode: string): ChildProjectNameParts {
  const base = `${parentName || ''}_${countryCode || ''}`;
  return {
    base,
    prefix: `${base}_`,
    orphanBase: `_${countryCode || ''}`,
    orphanPrefix: `_${countryCode || ''}_`,
  };
}

/**
 * Full stored value → the suffix portion the PM edits.
 *
 * Never trims the suffix: see the whitespace rule above. A stored value that
 * matches no known prefix is returned as-is so the PM can see and normalize a
 * legacy name (e.g. one written before the parent was renamed).
 */
export function deriveChildProjectSuffix(
  stored: string | undefined | null,
  parentName: string,
  countryCode: string,
): string {
  const { base, prefix, orphanBase, orphanPrefix } = childProjectNameParts(parentName, countryCode);
  const current = stored || '';
  if (current === base || current.trim() === base) return '';
  if (current.startsWith(prefix)) return current.substring(prefix.length);
  if (current.startsWith(orphanPrefix)) return current.substring(orphanPrefix.length);
  if (current === orphanBase || current.trim() === orphanBase) return '';
  return current;
}

/**
 * Suffix the PM typed → full value to store.
 *
 * The empty check is `=== ''`, NOT `.trim()`: a suffix of just spaces has to
 * survive the round-trip, otherwise a leading space can never be typed.
 */
export function composeChildProjectName(
  parentName: string,
  countryCode: string,
  suffix: string,
): string {
  const { base, prefix } = childProjectNameParts(parentName, countryCode);
  return suffix === '' ? base : `${prefix}${suffix}`;
}

/**
 * Submit-time normalization — the one place whitespace is cleaned up.
 *
 * Trims around the value, drops a suffix that is only whitespace (back to the
 * bare base), and preserves spaces inside the suffix. A legacy value that
 * doesn't carry the current prefix is only trimmed, never re-prefixed, so an
 * untouched legacy row saves exactly as it did before.
 */
export function normalizeChildProjectName(
  stored: string | undefined | null,
  parentName: string,
  countryCode: string,
): string {
  const { base, prefix } = childProjectNameParts(parentName, countryCode);
  const trimmed = (stored || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith(prefix)) {
    const suffix = trimmed.substring(prefix.length).trim();
    return suffix ? `${prefix}${suffix}` : base;
  }
  return trimmed;
}
